import { Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/common/components/ui/button';
import { Checkbox } from '@/common/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/common/components/ui/dropdown-menu';

export interface MultiSelectFilterOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectFilterOption[];
  label?: string;
  placeholder?: string;
  minWidth?: string;
  textSize?: string;
  className?: string;
}

export const MultiSelectFilter = ({
  values,
  onChange,
  options,
  label,
  placeholder,
  minWidth = 'min-w-[160px]',
  textSize = 'text-xxs',
  className,
}: MultiSelectFilterProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const handleToggle = (value: string) => {
    const newValues = values.includes(value)
      ? values.filter((v) => v !== value)
      : [...values, value];
    onChange(newValues);
  };

  const handleSelectAll = () => {
    onChange(options.map((opt) => opt.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getDisplayText = () => {
    if (values.length === 0) {
      return placeholder || 'Select...';
    }
    if (values.length === options.length) {
      return t('renewal.all');
    }
    if (values.length === 1) {
      const option = options.find((opt) => opt.value === values[0]);
      return option?.label || values[0];
    }
    return `${values.length} ${t('billing.filter.selected')}`;
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`h-7 ${textSize} justify-between ${minWidth} ${className ?? ''}`}
        >
          <span className="truncate">
            {label && <span className="text-muted-foreground">{label}: </span>}
            {getDisplayText()}
          </span>
          {values.length > 0 && values.length < options.length && (
            <Check className="ml-2 h-3 w-3 shrink-0 opacity-50" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[200px] p-2" align="start">
        <div className="space-y-2">
          <div className="flex gap-2 pb-2 border-b">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xxs flex-1"
              onClick={handleSelectAll}
            >
              {t('renewal.all')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xxs flex-1"
              onClick={handleClearAll}
            >
              {t('billing.filter.clear')}
            </Button>
          </div>
          <div className="space-y-1">
            {options.map((option) => (
              <div
                key={option.value}
                className="flex items-center space-x-2 hover:bg-accent rounded-sm p-1 w-full"
              >
                <Checkbox
                  id={option.value}
                  checked={values.includes(option.value)}
                  onCheckedChange={() => handleToggle(option.value)}
                />
                <label
                  htmlFor={option.value}
                  className="text-xs cursor-pointer"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
